/**
 * Per-AP SSID push outcome -> label, tone, bucket, order. Pure so it can be unit-tested
 * (`npm run test:unit`) and shared by the SSID dialog's result panel.
 *
 * Buckets mirror services/ssid_manager._SUMMARY_BUCKET exactly, so "N of M" counts the same on
 * both sides. The 2026-10-09 regression this pins: OAP101 a47d78ca5d1e REFUSED a rename
 * ("invalid parameters", uCentral error 1 with no `rejected` list) and the panel showed a green
 * "Applied" badge and "Renamed on 1 of 1 AP". A device rejection is now red and failed; an
 * apply where the AP dropped unsupported parts is amber but still counted as applied.
 *
 * 2026-10-11: that same AP (stock Edgecore EC-12.5.93) refuses EVERY config, so the rename stayed
 * "9 of 10" in red forever. A config-incompatible AP is now `excluded`: a NEUTRAL "Needs firmware"
 * chip, its own bucket, and never part of "N of M" -- the operation completes without it.
 */
import type { SsidPushOutcome, SsidPushRow, SsidPushSummary } from "@/types/domain";

/** Same buckets as services/ssid_manager.summarize_results, so both sides count alike. */
export const OUTCOME_BUCKET: Partial<Record<SsidPushOutcome, keyof SsidPushSummary>> = {
  applied: "applied_now",
  // Running the new config, minus parts the AP does not support -- still applied.
  applied_with_warnings: "applied_now",
  already_applied: "already",
  in_sync: "already",
  pending: "pending",
  unresolved: "pending",
  failed: "failed",
  // The AP refused the config and kept its old one. Never applied, whatever owgw now holds.
  rejected: "failed",
  // Not applied, and waiting will not fix it (owgw thinks the AP is newer) -- a re-save does.
  diverged: "failed",
  not_attempted: "not_reached",
  // Config-incompatible AP, not sent the change (services/config_compat). Its own bucket,
  // never "failed" and never in `total`.
  excluded: "excluded",
};

export const OUTCOME_LABEL: Record<SsidPushOutcome, string> = {
  applied: "Applied",
  applied_with_warnings: "Applied with warnings",
  already_applied: "Done earlier",
  in_sync: "Up to date",
  pending: "Still applying",
  unresolved: "Still applying",
  failed: "Failed",
  rejected: "Rejected by device",
  diverged: "Out of sync",
  not_attempted: "Not reached",
  excluded: "Needs firmware",
  skipped: "Skipped",
  not_applicable: "Not affected",
};

/** Failures first, then what needs a look, then what is still moving, then what is done. */
export const OUTCOME_ORDER: Record<SsidPushOutcome, number> = {
  rejected: 0,
  failed: 0,
  diverged: 0,
  pending: 1,
  unresolved: 1,
  not_attempted: 2,
  applied_with_warnings: 3,
  applied: 4,
  already_applied: 5,
  in_sync: 5,
  excluded: 6,
  skipped: 6,
  not_applicable: 6,
};

/** Badge classes (Meridian status tokens). Amber for warnings even though it counts as applied. */
export function outcomeTone(outcome: SsidPushOutcome): string {
  if (outcome === "applied_with_warnings") return "bg-warn-soft text-warn";
  const bucket = OUTCOME_BUCKET[outcome];
  if (bucket === "applied_now" || bucket === "already") return "bg-healthy-soft text-healthy";
  if (bucket === "pending") return "bg-warn-soft text-warn";
  if (bucket === "failed") return "bg-critical-soft text-critical";
  return "bg-surface-2 text-ink-muted";
}

/** Reason-line classes: a rejection reads in red so it cannot be skimmed past as grey detail. */
export function reasonTone(outcome: SsidPushOutcome): string {
  if (outcome === "rejected") return "text-critical";
  if (outcome === "applied_with_warnings") return "text-warn";
  return "text-ink-subtle";
}

/** Count per-device rows the way the backend does (applied includes earlier passes). */
export function summarizeRows(rows: SsidPushRow[]): SsidPushSummary {
  const out: Required<SsidPushSummary> = {
    total: 0, applied: 0, applied_now: 0, already: 0, pending: 0, failed: 0, not_reached: 0,
    excluded: 0,
  };
  for (const r of rows) {
    const bucket = OUTCOME_BUCKET[r.outcome];
    if (bucket) out[bucket] += 1;
  }
  // `excluded` is deliberately NOT part of total: "N of M" counts the APs that could be managed.
  out.applied = out.applied_now + out.already;
  out.total = out.applied + out.pending + out.failed + out.not_reached;
  return out;
}

/** Names of the rows with `outcome`, for the panel's one-line callouts. */
export function namesWith(rows: SsidPushRow[], outcome: SsidPushOutcome): string[] {
  return rows.filter((r) => r.outcome === outcome).map((r) => r.name);
}
